import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  Clock,
  TrendingUp,
  TrendingDown,
  UserX,
  Download,
  CalendarOff,
} from "lucide-react";
import schedules from "@/api/schedules";

function downloadCsv(filename, rows, columns) {
  const head = columns.map((c) => c.label).join(",");
  const body = rows
    .map((r) => columns.map((c) => JSON.stringify(r[c.key] ?? "")).join(","))
    .join("\n");
  const blob = new Blob([head + "\n" + body], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AnomaliesPanel({ open, onOpenChange, scheduleId }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("off");

  useEffect(() => {
    if (!open || !scheduleId) return;
    let alive = true;
    setLoading(true);
    schedules
      .anomalies(scheduleId)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open, scheduleId]);

  const off = data?.worked_on_off || [];
  const late = (data?.no_clock_in || []).filter((x) => x.reason === "late");
  const early = (data?.no_clock_in || []).filter((x) => x.reason === "early_leave");
  const absent = (data?.no_clock_in || []).filter((x) => x.reason === "absent");
  const noShift = data?.worked_no_shift || [];
  const comp = data?.compliance || { scheduled_hours: 0, worked_hours: 0, pct: null };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-xl"
        data-testid="schedule-anomalies-panel"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            {t("schedule.anomalies.title")}
          </SheetTitle>
          <SheetDescription>
            {t("schedule.anomalies.description")}
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="py-8 text-center text-xs text-muted-foreground">
            {t("common.loading")}
          </div>
        )}

        {data && (
          <div className="space-y-4 pt-4">
            {/* Compliance KPI */}
            <div
              className="rounded-lg border border-border bg-card p-4"
              data-testid="schedule-anomalies-compliance"
            >
              <div className="mb-2 flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("schedule.anomalies.compliance")}
                </div>
                <Badge
                  variant="outline"
                  className={`text-xs ${
                    comp.pct == null
                      ? ""
                      : comp.pct >= 95
                      ? "border-emerald-500/40 text-emerald-500"
                      : comp.pct >= 85
                      ? "border-amber-500/40 text-amber-500"
                      : "border-red-500/40 text-red-500"
                  }`}
                >
                  {comp.pct == null ? "—" : `${comp.pct}%`}
                </Badge>
              </div>
              <Progress value={comp.pct || 0} className="mb-2" />
              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span>
                  <TrendingUp className="mr-1 inline h-3 w-3" />
                  {comp.worked_hours} h {t("schedule.anomalies.worked")}
                </span>
                <span>
                  <Clock className="mr-1 inline h-3 w-3" />
                  {comp.scheduled_hours} h {t("schedule.anomalies.scheduled")}
                </span>
              </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid h-auto grid-cols-4 gap-1">
                <TabsTrigger value="off" className="flex flex-col gap-0.5 px-1 py-1.5 text-[10px]">
                  <span>🚨 OFF</span>
                  <Badge variant="secondary" className="text-[9px]">
                    {off.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="late" className="flex flex-col gap-0.5 px-1 py-1.5 text-[10px]">
                  <span>⏰ {t("schedule.anomalies.late")}</span>
                  <Badge variant="secondary" className="text-[9px]">
                    {late.length + early.length + absent.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="noshift" className="flex flex-col gap-0.5 px-1 py-1.5 text-[10px]">
                  <span>🟡 {t("schedule.anomalies.noShift")}</span>
                  <Badge variant="secondary" className="text-[9px]">
                    {noShift.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="kpi" className="flex flex-col gap-0.5 px-1 py-1.5 text-[10px]">
                  <span>📊 KPI</span>
                  <Badge variant="secondary" className="text-[9px]">
                    {comp.pct == null ? "—" : `${comp.pct}%`}
                  </Badge>
                </TabsTrigger>
              </TabsList>

              {/* WORKED_ON_OFF */}
              <TabsContent value="off">
                <div className="mb-2 flex justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-[10px]"
                    onClick={() =>
                      downloadCsv(
                        "anomalies_worked_on_off.csv",
                        off,
                        [
                          { key: "employee_name", label: "employee" },
                          { key: "day_date", label: "day" },
                          { key: "time_in", label: "time_in" },
                          { key: "time_out", label: "time_out" },
                          { key: "hours_worked", label: "hours" },
                        ]
                      )
                    }
                  >
                    <Download className="mr-1 h-3 w-3" />
                    CSV
                  </Button>
                </div>
                {off.length === 0 ? (
                  <Empty label={t("schedule.anomalies.empty")} />
                ) : (
                  off.map((x) => (
                    <Row
                      key={`${x.employee_name_norm}-${x.day_date}`}
                      title={x.employee_name}
                      subtitle={`${x.day_date} · ${x.time_in || "—"} – ${
                        x.time_out || "—"
                      }`}
                      right={`${x.hours_worked}h`}
                      icon={<CalendarOff className="h-4 w-4 text-pink-500" />}
                    />
                  ))
                )}
              </TabsContent>

              {/* LATE / EARLY / ABSENT */}
              <TabsContent value="late">
                {late.length + early.length + absent.length === 0 ? (
                  <Empty label={t("schedule.anomalies.empty")} />
                ) : (
                  <div className="space-y-3">
                    {absent.length > 0 && (
                      <Group title={t("schedule.anomalies.absent")} count={absent.length}>
                        {absent.map((x) => (
                          <Row
                            key={`${x.employee_name_norm}-${x.day_date}`}
                            title={x.employee_name}
                            subtitle={`${x.day_date} · ${t(
                              "schedule.anomalies.scheduledLabel"
                            )} ${x.scheduled_in}-${x.scheduled_out}`}
                            right={t("schedule.anomalies.noClock")}
                            icon={<UserX className="h-4 w-4 text-red-500" />}
                          />
                        ))}
                      </Group>
                    )}
                    {late.length > 0 && (
                      <Group title={t("schedule.anomalies.late")} count={late.length}>
                        {late.map((x) => (
                          <Row
                            key={`${x.employee_name_norm}-${x.day_date}-late`}
                            title={x.employee_name}
                            subtitle={`${x.day_date} · ${t(
                              "schedule.anomalies.scheduledLabel"
                            )} ${x.scheduled_in} · ${t(
                              "schedule.anomalies.clockedLabel"
                            )} ${x.clock_in}`}
                            right={`+${x.delta_minutes}min`}
                            icon={<Clock className="h-4 w-4 text-amber-500" />}
                          />
                        ))}
                      </Group>
                    )}
                    {early.length > 0 && (
                      <Group title={t("schedule.anomalies.earlyLeave")} count={early.length}>
                        {early.map((x) => (
                          <Row
                            key={`${x.employee_name_norm}-${x.day_date}-early`}
                            title={x.employee_name}
                            subtitle={`${x.day_date} · ${t(
                              "schedule.anomalies.scheduledLabel"
                            )} ${x.scheduled_out} · ${t(
                              "schedule.anomalies.clockedLabel"
                            )} ${x.clock_out}`}
                            right={`-${x.delta_minutes}min`}
                            icon={<TrendingDown className="h-4 w-4 text-orange-500" />}
                          />
                        ))}
                      </Group>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* WORKED_NO_SHIFT */}
              <TabsContent value="noshift">
                <div className="mb-2 flex justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-[10px]"
                    onClick={() =>
                      downloadCsv(
                        "anomalies_worked_no_shift.csv",
                        noShift,
                        [
                          { key: "employee_name", label: "employee" },
                          { key: "day_date", label: "day" },
                          { key: "time_in", label: "time_in" },
                          { key: "time_out", label: "time_out" },
                          { key: "hours_worked", label: "hours" },
                        ]
                      )
                    }
                  >
                    <Download className="mr-1 h-3 w-3" />
                    CSV
                  </Button>
                </div>
                {noShift.length === 0 ? (
                  <Empty label={t("schedule.anomalies.empty")} />
                ) : (
                  noShift.map((x) => (
                    <Row
                      key={`${x.employee_name_norm}-${x.day_date}`}
                      title={x.employee_name}
                      subtitle={`${x.day_date} · ${x.time_in || "—"} – ${
                        x.time_out || "—"
                      }`}
                      right={`${x.hours_worked}h`}
                      icon={
                        <AlertTriangle className="h-4 w-4 text-yellow-500" />
                      }
                    />
                  ))
                )}
                <div className="mt-3 rounded-md border border-yellow-500/30 bg-yellow-500/5 p-2 text-[10px] text-yellow-600">
                  {t("schedule.anomalies.noShiftHint")}
                </div>
              </TabsContent>

              {/* KPI tab */}
              <TabsContent value="kpi">
                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-card p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {t("schedule.anomalies.compliance")}
                    </div>
                    <div className="my-2 text-3xl font-bold">
                      {comp.pct == null ? "—" : `${comp.pct}%`}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {comp.worked_hours}h / {comp.scheduled_hours}h{" "}
                      {t("schedule.anomalies.kpiFormula")}
                    </div>
                  </div>
                  <div className="rounded-md border border-border bg-card p-3 text-[11px]">
                    <div className="mb-1 font-semibold">
                      {t("schedule.anomalies.kpiNotesTitle")}
                    </div>
                    <ul className="list-disc space-y-1 pl-4 text-muted-foreground">
                      <li>{t("schedule.anomalies.kpiNote1")}</li>
                      <li>{t("schedule.anomalies.kpiNote2")}</li>
                      <li>{t("schedule.anomalies.kpiNote3")}</li>
                    </ul>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Group({ title, count, children }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span>{title}</span>
        <Badge variant="secondary" className="text-[9px]">
          {count}
        </Badge>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ title, subtitle, right, icon }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-card p-2">
      <div className="flex h-7 w-7 items-center justify-center rounded-md bg-muted">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium">{title}</div>
        <div className="truncate text-[10px] text-muted-foreground">{subtitle}</div>
      </div>
      <div className="text-[11px] font-mono text-muted-foreground">{right}</div>
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="rounded-md border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
      {label}
    </div>
  );
}
